// główny rząd mobilnego headera (Wise) - logo + ikony + wyszukiwarka

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Person, JournalText, Cart3, List, Search as SearchIcon } from 'react-bootstrap-icons';
import classnames from 'classnames';

import { useAppNavigate } from 'hooks';
import { useDispatch, useSelector, reduxActions } from 'store';
import { useGetUiSettings, useGetCartsAll } from 'api';

import fallbackLogo from 'assets/logo.svg';

import styles from 'theme/components/wise/WiseHeaderMain/WiseHeaderMain.module.scss';

const WiseHeaderMain = () => {
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const { t } = useTranslation();

  const { profile } = useSelector((state) => state.auth);
  const { currentCartId } = useSelector((state) => state.cart);
  const { isMobileMenu } = useSelector((state) => state.ui);

  const [searchKeyword, setSearchKeyword] = useState('');

  const { data: uiSettings, isLoading: isUiSettingsLoading } = useGetUiSettings();
  const logoSrc = uiSettings?.logo_file || fallbackLogo;

  const isLogged = !!profile && profile.role !== 'ROLE_OPEN_PROFILE';

  // Lista koszyków - potrzebna żeby kliknięcie ikony koszyka prowadziło do realnego ID,
  // a nie do /cart/0 (nieistniejący koszyk).
  const { data: cartsData } = useGetCartsAll({
    enabled: isLogged,
    onSuccess: (data) => {
      if (!currentCartId && data.items[0]?.id) {
        dispatch(reduxActions.setCurrentCartId(data.items[0].id));
      }
    }
  });

  const goCart = () => {
    const targetId = currentCartId ?? cartsData?.items[0]?.id;
    if (targetId) {
      navigate(`/cart/${targetId}`);
    } else {
      // Brak koszyków - lądowanie na liście koszyków (dashboard) gdzie można utworzyć nowy
      navigate('/dashboard/carts');
    }
  };

  const goHome = () => {
    navigate('/');
    setTimeout(() => {
      dispatch(reduxActions.setSearchKeyword(''));
    }, 10);
  };

  const submitSearch = () => {
    const phrase = searchKeyword.trim();
    dispatch(reduxActions.setSearchKeyword(phrase));
    navigate(`/products?search_keyword=${encodeURIComponent(phrase)}`);
  };

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Components-Wise-WiseHeaderMain'
      )}>
      <div className={styles.topRow}>
        <button onClick={goHome} className={styles.logo} aria-label="logo">
          {!isUiSettingsLoading && <img src={logoSrc} alt="logo" />}
        </button>

        {isLogged && (
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => navigate('/dashboard')}
              aria-label={t('Twoje konto') || 'account'}>
              <Person />
            </button>
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => navigate('/dashboard/shopping-lists')}
              aria-label={t('Listy zakupowe') || 'shopping-lists'}>
              <JournalText />
            </button>
            <button
              type="button"
              className={classnames(styles.iconButton, styles.cartButton)}
              onClick={goCart}
              aria-label={t('Koszyk') || 'cart'}>
              <Cart3 />
            </button>
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => dispatch(reduxActions.setIsMobileMenu(!isMobileMenu))}
              aria-label={t('Menu') || 'menu'}>
              <List />
            </button>
          </div>
        )}
      </div>

      <div className={styles.searchRow}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder={t('Wyszukaj') || 'Wyszukaj'}
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submitSearch();
            }
          }}
        />
        <button
          type="button"
          className={styles.searchIcon}
          onClick={submitSearch}
          aria-label={t('Szukaj') || 'search'}>
          <SearchIcon />
        </button>
      </div>
    </div>
  );
};

export default WiseHeaderMain;
